import { useState, useCallback } from "react";
import { Check, Copy, History } from "lucide-react";
import { useTransactions } from "@/hooks/use-transactions";
import { t } from "@/lib/i18n/messages";
import { formatCurrency, formatDate } from "@/lib/utils/format";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/ui/empty-state";
import { cn } from "@/lib/utils";
import type { Locale } from "@/types/auth";
import type { TransactionStatus } from "@/types/wallet";

interface TopUpHistoryProps {
  locale: Locale;
}

const STATUS_FILTERS: (TransactionStatus | "all")[] = [
  "all",
  "completed",
  "pending",
  "failed",
];

function truncateId(id: string | null | undefined): string {
  if (!id) return "—";
  if (id.length <= 14) return id;
  return `${id.slice(0, 8)}…${id.slice(-4)}`;
}

function StatusBadge({ status, locale }: { status: TransactionStatus; locale: Locale }) {
  const variantMap: Record<TransactionStatus, "success" | "warning" | "error"> = {
    completed: "success",
    pending: "warning",
    failed: "error",
  };
  const labelMap: Record<TransactionStatus, string> = {
    completed: t(locale, "completed"),
    pending: t(locale, "pending"),
    failed: t(locale, "failed"),
  };
  return <Badge variant={variantMap[status]}>{labelMap[status]}</Badge>;
}

export function TopUpHistory({ locale }: TopUpHistoryProps) {
  const [statusFilter, setStatusFilter] = useState<TransactionStatus | "all">("all");
  const [allRows, setAllRows] = useState<import("@/types/wallet").Transaction[]>([]);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const { data, loading, meta, setPage, setStatus, currentParams } = useTransactions({
    initialType: "Fund",
    initialPerPage: 10,
  });

  // Merge new data into allRows on each page load
  const currentPage = currentParams.page ?? 1;
  if (currentPage === 1 && data.length > 0 && allRows.length === 0) {
    setAllRows(data);
  }

  const handleStatusChange = (s: TransactionStatus | "all") => {
    setStatusFilter(s);
    setAllRows([]);
    setStatus(s === "all" ? undefined : s);
  };

  const handleLoadMore = useCallback(() => {
    setAllRows((prev) => [...prev, ...data]);
    setPage(currentPage + 1);
  }, [data, currentPage, setPage]);

  const handleCopy = (id: string) => {
    navigator.clipboard.writeText(id).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const rows = allRows.length > 0 ? allRows : data;
  const hasMore = meta ? currentPage < meta.last_page : false;

  return (
    <div className="rounded-lg border border-border bg-card">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-6 border-b border-border">
        <h3 className="text-base font-semibold text-foreground">
          {t(locale, "topUpHistory")}
        </h3>
        {/* Status filter */}
        <select
          value={statusFilter}
          onChange={(e) => handleStatusChange(e.target.value as TransactionStatus | "all")}
          className={cn(
            "text-sm border border-border rounded-md px-3 py-1.5 bg-background text-foreground",
            "focus:outline-none focus:ring-2 focus:ring-primary/40"
          )}
          aria-label={t(locale, "filter")}
        >
          {STATUS_FILTERS.map((s) => (
            <option key={s} value={s}>
              {s === "all" ? t(locale, "all") : t(locale, s)}
            </option>
          ))}
        </select>
      </div>

      {/* Content */}
      {loading && rows.length === 0 ? (
        /* Skeleton — 5 rows */
        <div className="divide-y divide-border" aria-hidden="true">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex items-center gap-4 p-4">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-5 w-16 rounded-full" />
              <Skeleton className="h-4 w-28 ms-auto" />
            </div>
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={History}
          title={t(locale, "noTransactions")}
          className="border-0 m-6"
        />
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="px-4 py-3 text-start font-medium text-foreground-secondary">{t(locale, "date")}</th>
                  <th className="px-4 py-3 text-start font-medium text-foreground-secondary">{t(locale, "amount")}</th>
                  <th className="px-4 py-3 text-start font-medium text-foreground-secondary">{t(locale, "status")}</th>
                  <th className="px-4 py-3 text-start font-medium text-foreground-secondary">{t(locale, "transactionId")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map((row, idx) => (
                  <tr key={row.id ?? `row-${idx}`} className="hover:bg-muted/20 transition-colors">
                    <td className="px-4 py-3 text-foreground whitespace-nowrap">
                      {formatDate(row.created_at, locale)}
                    </td>
                    <td className="px-4 py-3 font-medium text-foreground whitespace-nowrap">
                      {formatCurrency(row.total_amount, locale)}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={row.status} locale={locale} />
                    </td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => handleCopy(row.id)}
                        className="flex items-center gap-1.5 text-foreground-secondary hover:text-foreground transition-colors font-mono text-xs"
                        title={t(locale, "copyId")}
                        aria-label={t(locale, "copyId")}
                      >
                        <span>{truncateId(row.id)}</span>
                        {copiedId === row.id ? (
                          <Check className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
                        ) : (
                          <Copy className="h-3.5 w-3.5 flex-shrink-0" />
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile card list */}
          <div className="md:hidden divide-y divide-border">
            {rows.map((row, idx) => (
              <div key={row.id ?? `row-${idx}`} className="p-4 space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-foreground">
                    {formatCurrency(row.total_amount, locale)}
                  </span>
                  <StatusBadge status={row.status} locale={locale} />
                </div>
                <div className="text-xs text-foreground-secondary">
                  {formatDate(row.created_at, locale)}
                </div>
                <button
                  type="button"
                  onClick={() => handleCopy(row.id)}
                  className="flex items-center gap-1 text-xs text-foreground-secondary hover:text-foreground font-mono"
                  aria-label={t(locale, "copyId")}
                >
                  <span>{truncateId(row.id)}</span>
                  {copiedId === row.id ? (
                    <Check className="h-3 w-3 text-green-500" />
                  ) : (
                    <Copy className="h-3 w-3" />
                  )}
                </button>
              </div>
            ))}
          </div>

          {/* Load more */}
          {hasMore && (
            <div className="p-4 border-t border-border text-center">
              <button
                type="button"
                onClick={handleLoadMore}
                disabled={loading}
                className={cn(
                  "px-4 py-2 rounded-md text-sm font-medium",
                  "border border-border hover:bg-muted/50 text-foreground",
                  "transition-colors duration-150",
                  loading && "opacity-50 cursor-not-allowed"
                )}
              >
                {loading ? t(locale, "loading") : t(locale, "loadMore")}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
